import type { ChapterPlan, RoadmapChunkRecord, RoadmapPlanningState, RoadmapWarning } from '../types';

export interface RoadmapChunkRange {
  chunkIndex: number;
  startChapter: number;
  endChapter: number;
}

export class RoadmapPlanningError extends Error {
  readonly chunkIndex?: number;
  readonly chapterRange?: string;
  readonly missingField?: string;
  readonly requestId?: string;
  readonly developerMessage: string;

  constructor(
    userMessage: string,
    meta: {
      chunkIndex?: number;
      chapterRange?: string;
      missingField?: string;
      requestId?: string;
      developerMessage?: string;
    },
    cause?: unknown,
  ) {
    super(userMessage);
    this.name = 'RoadmapPlanningError';
    this.chunkIndex = meta.chunkIndex;
    this.chapterRange = meta.chapterRange;
    this.missingField = meta.missingField;
    this.requestId = meta.requestId;
    this.developerMessage = meta.developerMessage || userMessage;
    if (cause instanceof Error && cause.stack) {
      this.stack = cause.stack;
    }
  }
}

export const DEFAULT_ROADMAP_BATCH_SIZE = 10;

export function getRoadmapChunkRanges(
  targetChapterCount: number,
  batchSize = DEFAULT_ROADMAP_BATCH_SIZE,
): RoadmapChunkRange[] {
  const ranges: RoadmapChunkRange[] = [];
  let chunkIndex = 0;
  for (let start = 1; start <= targetChapterCount; start += batchSize) {
    chunkIndex += 1;
    ranges.push({
      chunkIndex,
      startChapter: start,
      endChapter: Math.min(start + batchSize - 1, targetChapterCount),
    });
  }
  return ranges;
}

export function formatChapterRange(start: number, end: number): string {
  return start === end ? `${start}` : `${start}-${end}`;
}

export function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
}

export function normalizeChapterPlan(raw: unknown, fallbackChapterNumber?: number): ChapterPlan | null {
  if (!raw || typeof raw !== 'object') return null;
  const plan = raw as Record<string, unknown>;
  const chapterNumber =
    typeof plan.chapterNumber === 'number'
      ? plan.chapterNumber
      : typeof fallbackChapterNumber === 'number'
        ? fallbackChapterNumber
        : null;
  if (chapterNumber === null || !Number.isFinite(chapterNumber)) return null;

  const title =
    typeof plan.title === 'string' && plan.title.trim()
      ? plan.title.trim()
      : typeof plan.chapterTitle === 'string' && plan.chapterTitle.trim()
        ? plan.chapterTitle.trim()
        : '';
  const summary = typeof plan.summary === 'string' ? plan.summary.trim() : '';
  if (!title || !summary) return null;

  return {
    id:
      typeof plan.id === 'string' && plan.id.trim()
        ? plan.id
        : `plan_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    chapterNumber,
    title,
    pov: typeof plan.pov === 'string' ? plan.pov : undefined,
    summary,
    requiredBeats: normalizeStringArray(plan.requiredBeats ?? plan.beats ?? plan.scenes),
    forbiddenMoves: normalizeStringArray(plan.forbiddenMoves),
    continuityNotes: normalizeStringArray(plan.continuityNotes),
    characterFocus: normalizeStringArray(plan.characterFocus),
    locationFocus: normalizeStringArray(plan.locationFocus),
    status: 'planned',
    updatedAt: new Date().toISOString(),
  };
}

export function normalizeRoadmapChunkPayload(
  raw: unknown,
  range: RoadmapChunkRange,
  requestId?: string,
): RoadmapChunkRecord {
  const topLevel = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const topLevelKeys = Object.keys(topLevel);
  const plansSource =
    topLevel.plans ??
    topLevel.chapters ??
    topLevel.chapterPlans ??
    topLevel.roadmap;

  if (plansSource === undefined || plansSource === null) {
    const missingField = topLevelKeys.includes('plans')
      ? 'plans'
      : topLevelKeys.includes('chapters')
        ? 'chapters'
        : topLevelKeys.includes('chapterPlans')
          ? 'chapterPlans'
          : 'plans';
    throw new RoadmapPlanningError(
      `Roadmap chunk ${range.chunkIndex} (${formatChapterRange(range.startChapter, range.endChapter)}) is missing chapter plans.`,
      {
        chunkIndex: range.chunkIndex,
        chapterRange: formatChapterRange(range.startChapter, range.endChapter),
        missingField,
        requestId,
        developerMessage: `Chunk payload keys: ${topLevelKeys.join(', ') || '(none)'}`,
      },
    );
  }

  if (!Array.isArray(plansSource)) {
    throw new RoadmapPlanningError(
      `Roadmap chunk ${range.chunkIndex} (${formatChapterRange(range.startChapter, range.endChapter)}) returned a non-array plans field.`,
      {
        chunkIndex: range.chunkIndex,
        chapterRange: formatChapterRange(range.startChapter, range.endChapter),
        missingField: 'plans',
        requestId,
        developerMessage: `Expected array, received ${typeof plansSource}`,
      },
    );
  }

  const plans: ChapterPlan[] = [];
  for (const entry of plansSource) {
    const normalized = normalizeChapterPlan(entry);
    if (normalized) plans.push(normalized);
  }

  validateChunkPlans(plans, range, requestId);

  return {
    chunkIndex: range.chunkIndex,
    startChapter: range.startChapter,
    endChapter: range.endChapter,
    requestId,
    plans,
    validatedAt: new Date().toISOString(),
    status: 'valid',
  };
}

export function validateChunkPlans(
  plans: ChapterPlan[],
  range: RoadmapChunkRange,
  requestId?: string,
): void {
  if (!Array.isArray(plans)) {
    throw new RoadmapPlanningError(
      `Roadmap chunk ${range.chunkIndex} (${formatChapterRange(range.startChapter, range.endChapter)}) has no chapter array.`,
      {
        chunkIndex: range.chunkIndex,
        chapterRange: formatChapterRange(range.startChapter, range.endChapter),
        missingField: 'plans',
        requestId,
      },
    );
  }

  const expectedCount = range.endChapter - range.startChapter + 1;
  const inRange = plans.filter(
    (plan) => plan.chapterNumber >= range.startChapter && plan.chapterNumber <= range.endChapter,
  );

  if (inRange.length < expectedCount) {
    throw new RoadmapPlanningError(
      `Roadmap chunk ${range.chunkIndex} (${formatChapterRange(range.startChapter, range.endChapter)}) returned ${inRange.length} of ${expectedCount} required chapters.`,
      {
        chunkIndex: range.chunkIndex,
        chapterRange: formatChapterRange(range.startChapter, range.endChapter),
        missingField: 'chapterNumber',
        requestId,
        developerMessage: `Received chapter numbers: ${inRange.map((p) => p.chapterNumber).join(', ') || '(none)'}`,
      },
    );
  }

  for (const plan of inRange) {
    if (!plan.title?.trim()) {
      throw new RoadmapPlanningError(
        `Roadmap chunk ${range.chunkIndex} chapter ${plan.chapterNumber} is missing a title.`,
        {
          chunkIndex: range.chunkIndex,
          chapterRange: formatChapterRange(range.startChapter, range.endChapter),
          missingField: 'title',
          requestId,
        },
      );
    }
    if (!plan.summary?.trim()) {
      throw new RoadmapPlanningError(
        `Roadmap chunk ${range.chunkIndex} chapter ${plan.chapterNumber} is missing a summary.`,
        {
          chunkIndex: range.chunkIndex,
          chapterRange: formatChapterRange(range.startChapter, range.endChapter),
          missingField: 'summary',
          requestId,
        },
      );
    }
    if (!Array.isArray(plan.requiredBeats)) {
      throw new RoadmapPlanningError(
        `Roadmap chunk ${range.chunkIndex} chapter ${plan.chapterNumber} is missing requiredBeats.`,
        {
          chunkIndex: range.chunkIndex,
          chapterRange: formatChapterRange(range.startChapter, range.endChapter),
          missingField: 'requiredBeats',
          requestId,
        },
      );
    }
  }
}

export function isValidRoadmapChunkRecord(record: RoadmapChunkRecord | undefined): record is RoadmapChunkRecord {
  if (!record || record.status !== 'valid' || !Array.isArray(record.plans)) return false;
  try {
    validateChunkPlans(record.plans, {
      chunkIndex: record.chunkIndex,
      startChapter: record.startChapter,
      endChapter: record.endChapter,
    }, record.requestId);
    return true;
  } catch {
    return false;
  }
}

export function assembleRoadmapFromChunks(
  chunks: RoadmapChunkRecord[],
  targetChapterCount: number,
): ChapterPlan[] {
  const byChapter = new Map<number, ChapterPlan>();
  for (const chunk of chunks) {
    if (!isValidRoadmapChunkRecord(chunk)) continue;
    for (const plan of chunk.plans) {
      byChapter.set(plan.chapterNumber, plan);
    }
  }
  return Array.from(byChapter.values()).sort((a, b) => a.chapterNumber - b.chapterNumber);
}

export function validateAssembledRoadmap(
  plans: ChapterPlan[],
  targetChapterCount: number,
  requestId?: string,
): RoadmapWarning[] {
  if (!Array.isArray(plans)) {
    throw new RoadmapPlanningError('Assembled roadmap is missing chapter plans.', {
      missingField: 'plans',
      requestId,
      developerMessage: 'Assembly received undefined plans array',
    });
  }

  if (plans.length !== targetChapterCount) {
    throw new RoadmapPlanningError(
      `Roadmap assembly produced ${plans.length} chapters but ${targetChapterCount} are required.`,
      {
        missingField: 'chapterCount',
        requestId,
        developerMessage: `Chapter numbers present: ${plans.map((p) => p.chapterNumber).join(', ')}`,
      },
    );
  }

  const numbers = plans.map((plan) => plan.chapterNumber);
  const unique = new Set(numbers);
  if (unique.size !== numbers.length) {
    throw new RoadmapPlanningError('Roadmap assembly contains duplicate chapter numbers.', {
      missingField: 'chapterNumber',
      requestId,
      developerMessage: `Duplicate chapter numbers: ${numbers.join(', ')}`,
    });
  }

  for (let i = 1; i <= targetChapterCount; i += 1) {
    if (!unique.has(i)) {
      throw new RoadmapPlanningError(`Roadmap assembly is missing chapter ${i}.`, {
        missingField: 'chapterNumber',
        requestId,
      });
    }
  }

  const sorted = [...numbers].sort((a, b) => a - b);
  for (let i = 0; i < sorted.length; i += 1) {
    if (sorted[i] !== i + 1) {
      throw new RoadmapPlanningError('Roadmap chapters are not ordered 1 through N.', {
        missingField: 'chapterNumber',
        requestId,
        developerMessage: `Observed order: ${sorted.join(', ')}`,
      });
    }
  }

  return [];
}

export function buildInitialRoadmapPlanningState(targetChapterCount: number): RoadmapPlanningState {
  return {
    targetChapterCount,
    batchSize: DEFAULT_ROADMAP_BATCH_SIZE,
    chunks: [],
    phase: 'pending',
    updatedAt: new Date().toISOString(),
  };
}

export function mergeRoadmapPlanningState(
  existing: RoadmapPlanningState | undefined | null,
  targetChapterCount: number,
): RoadmapPlanningState {
  if (
    existing &&
    existing.targetChapterCount === targetChapterCount &&
    Array.isArray(existing.chunks)
  ) {
    return {
      ...existing,
      chunks: existing.chunks.filter((chunk) => isValidRoadmapChunkRecord(chunk)),
      updatedAt: new Date().toISOString(),
    };
  }
  return buildInitialRoadmapPlanningState(targetChapterCount);
}

export function upsertRoadmapChunk(
  state: RoadmapPlanningState,
  chunk: RoadmapChunkRecord,
): RoadmapPlanningState {
  const chunks = [...state.chunks.filter((entry) => entry.chunkIndex !== chunk.chunkIndex), chunk].sort(
    (a, b) => a.chunkIndex - b.chunkIndex,
  );
  return {
    ...state,
    chunks,
    phase: 'chunking',
    updatedAt: new Date().toISOString(),
  };
}

export function allRequiredChunksPresent(
  state: RoadmapPlanningState,
  ranges: RoadmapChunkRange[],
): boolean {
  return ranges.every((range) =>
    isValidRoadmapChunkRecord(state.chunks.find((chunk) => chunk.chunkIndex === range.chunkIndex)),
  );
}

export function getMissingChunkRanges(
  state: RoadmapPlanningState,
  ranges: RoadmapChunkRange[],
): RoadmapChunkRange[] {
  return ranges.filter(
    (range) =>
      !isValidRoadmapChunkRecord(state.chunks.find((chunk) => chunk.chunkIndex === range.chunkIndex)),
  );
}

export function logRoadmapPlanningDeveloperError(error: unknown): void {
  if (error instanceof RoadmapPlanningError) {
    console.error('[roadmapPlanning]', error.developerMessage, {
      chunkIndex: error.chunkIndex,
      chapterRange: error.chapterRange,
      missingField: error.missingField,
      requestId: error.requestId,
      stack: error.stack,
    });
    return;
  }
  if (error instanceof Error) {
    console.error('[roadmapPlanning]', error.message, error.stack);
    return;
  }
  console.error('[roadmapPlanning]', error);
}

export function toUserFacingRoadmapError(error: unknown): string {
  if (error instanceof RoadmapPlanningError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Roadmap planning failed unexpectedly.';
}
